import { activeTab, normalisePath, type MobileTab } from '@l2u/web-shared';

/**
 * The DRIVER portal's mobile navigation model.
 *
 * Five tabs, which is as many as a bottom bar holds. Kept here rather than in
 * web-shared for the same reason the admin app keeps its own: a tab set and a
 * route-title table are answers about one app's routes, and nothing shared can
 * enumerate them. The pathname primitives underneath — MobileTab, activeTab,
 * normalisePath — come from web-shared.
 *
 * React-free and route-object-free on purpose, so it can be unit-tested without a
 * DOM (this workspace has no component test environment).
 */

const DRIVER_TABS: MobileTab[] = [
  {
    key: 'details',
    label: 'My details',
    to: '/details',
    icon: 'user-check',
    end: false,
    extraPrefixes: [],
  },
  {
    key: 'questionnaire',
    label: 'Questionnaire',
    to: '/questionnaire',
    icon: 'clipboard-check',
    end: false,
    extraPrefixes: [],
  },
  {
    key: 'training',
    label: 'Training',
    to: '/training',
    icon: 'graduation-cap',
    end: false,
    extraPrefixes: [],
  },
  {
    key: 'library',
    label: 'Library',
    to: '/library',
    icon: 'book',
    end: false,
    extraPrefixes: [],
  },
  {
    key: 'file-notes',
    label: 'File notes',
    to: '/file-notes',
    icon: 'file-text',
    end: false,
    extraPrefixes: [],
  },
];

/**
 * Questionnaire: the action count (see driverQuestionnaireBadge). Training,
 * Library and File notes: GET /api/driver/me/nav-counts — modules not yet
 * completed, documents not opened since they last changed, and published file
 * notes with something the driver has not read.
 */
export type DriverTabBadges = {
  questionnaire?: number | undefined;
  training?: number | undefined;
  library?: number | undefined;
  'file-notes'?: number | undefined;
};

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

// What the bar reads after the count: "Training (2 training modules to
// complete)". The same sentences as the company sidebar's badges.
const BADGE_LABELS: Record<keyof DriverTabBadges, (n: number) => string> = {
  questionnaire: () => 'items need your action',
  training: (n) => `training ${plural(n, 'module', 'modules')} to complete`,
  library: (n) => `new ${plural(n, 'document', 'documents')} to read`,
  'file-notes': (n) => `unread file ${plural(n, 'note', 'notes')}`,
};

function isBadgedKey(key: string): key is keyof DriverTabBadges {
  return key in BADGE_LABELS;
}

/**
 * The ordered bottom-tab descriptors for the driver portal, badged for THIS
 * driver: the Questionnaire tab carries the number of things the driver has to
 * action (vendorActionBadgeCount — 1 per unsubmitted questionnaire, plus the
 * returned answers in submitted ones; DriverShell falls back to
 * `pending_questionnaire_count` while that loads); Training and Library carry
 * the server's nav counts (see DriverTabBadges).
 * Zero or absent means no bubble. Returns fresh objects so the shared list is
 * never mutated.
 *
 * `base` is where the driver family is mounted on this host — '' on the driver
 * host, '/d' on the shared one — so a tab links to the same place the router
 * mounted the page. Required rather than defaulted: a forgotten base would
 * link every tab to the legacy path, which on the driver host is a forward.
 *
 * A function rather than the exported array, matching the admin app's
 * `tabsForRole`: it is the tested seam callers already go through, so a future
 * per-driver tab set (a driver whose company runs no training, say) needs no
 * change at the call site.
 */
/**
 * The number the Questionnaire tab shows. `actionCount` is null until BOTH the
 * question and submission lists have loaded (useVendorActionCount); until then
 * the server's `pending_questionnaire_count` stands in, so the bubble does not
 * blank out or jump from a partial count. Once the lists are in, the action
 * count REPLACES the server count — never added to it. An unsubmitted
 * questionnaire contributes exactly 1 to it (vendorActionBadgeCount), which is
 * what the server count means too, so the switch-over never jumps 1 → 40.
 */
export function driverQuestionnaireBadge(
  actionCount: number | null,
  pendingQuestionnaireCount: number | null | undefined,
): number {
  return actionCount ?? pendingQuestionnaireCount ?? 0;
}

export function driverTabs(base: string, badges: DriverTabBadges = {}): MobileTab[] {
  return DRIVER_TABS.map((tab) => {
    const placed = { ...tab, to: `${base}${tab.to}` };
    if (!isBadgedKey(tab.key)) return placed;
    const count = badges[tab.key] ?? 0;
    return count > 0
      ? { ...placed, badge: count, badgeLabel: BADGE_LABELS[tab.key](count) }
      : placed;
  });
}

// Every route the driver shell renders, relative to the family root. The root
// itself redirects to /details, so it titles as that rather than as something
// the driver never sees.
const ROUTE_TITLES: Record<string, string> = {
  '/': 'My details',
  '/details': 'My details',
  '/questionnaire': 'Questionnaire',
  '/training': 'Training',
  '/library': 'Library',
  '/file-notes': 'File notes',
};

// Detail routes, titled as the list they belong to: one segment below it.
const DETAIL_TITLES: Record<string, string> = {
  '/file-notes': 'File notes',
};

function titleFor(path: string): string | null {
  const exact = ROUTE_TITLES[path];
  if (exact !== undefined) return exact;
  return DETAIL_TITLES[path.slice(0, path.lastIndexOf('/'))] ?? null;
}

/**
 * App-bar title for a driver-portal pathname, or null when the route is unknown.
 *
 * `base` is where the driver family is mounted on this host: '' on the driver
 * host, '/d' on the shared one (familyBase in lib/audienceRoutes.ts).
 *
 * The one detail route, a file note (/file-notes/:id), titles as its list.
 * There is no back chevron to drive: the note opens from an email as often as
 * from the list, so the page carries its own link back instead.
 */
export function driverAppBarTitle(pathname: string, base: string): string | null {
  const path = normalisePath(pathname);
  if (base === '') return titleFor(path);
  // Segment boundary, so '/dx' is not read as '/d' + 'x'.
  if (path === base) return ROUTE_TITLES['/'] ?? null;
  if (!path.startsWith(`${base}/`)) return null;
  return titleFor(path.slice(base.length));
}

/**
 * The driver portal's heading — the desktop header, and the phone app bar's
 * accessible name off the titled routes: the tenant's word for a driver, then
 * "Onboarding", capitalised ("Driver Onboarding").
 */
export function driverOnboardingHeading(driverLabel: string): string {
  return `${driverLabel} Onboarding`;
}

/** Re-exported so the shell has one import for its whole nav model. */
export { activeTab };
